/**
 * Cookie consent, one switch.
 *
 * Three non-essential things run on this site: Google Tag Manager, Google
 * Analytics 4, and FullStory session recording. Until 2026-09-09 all three
 * loaded on the first byte of every page, on both entry points, with no way
 * for a visitor to say no. The privacy policy carried a TODO where the
 * prior-consent wording should be.
 *
 * The model is prior consent for everybody, not only the EEA. Denied is the
 * default, Decline is exactly as easy as Accept, and the choice is one bit:
 * analytics and session recording travel together, because a visitor is
 * being asked one question ("may we watch how this gets used") and splitting
 * it into a control panel makes the honest answer harder to give than the
 * agreeable one.
 *
 * The first half of this lives in an inline snippet at the top of
 * client/index.html and astro/src/layouts/BaseLayout.astro: Consent Mode v2
 * defaults have to be in the dataLayer BEFORE gtag.js reads it, which is
 * long before this module is parsed. Those two snippets are byte-identical
 * and a test fails when they drift. This module owns everything after the
 * first paint: reading the choice, writing it, and telling the tags.
 */

export const CONSENT_STORAGE_KEY = 'ss_cookie_consent'
export const CONSENT_TIME_KEY = 'ss_cookie_consent_at'
export const CONSENT_VERSION_KEY = 'ss_cookie_consent_v'

/**
 * What the answer was an answer to.
 *
 * The cookie policy promises that if we add a category of storage or a new
 * provider, we ask again before it is set, rather than treating a yes given
 * to a shorter list as covering a longer one. This number is how that
 * promise is kept: raise it in the same change that adds the category, and
 * every stored answer stops counting. It is not a version of the document.
 *
 * 1: strictly necessary, preferences, analytics (Google), session recording
 *    (FullStory).
 */
export const CONSENT_VERSION = '1'

/**
 * How long an answer stands before we ask again: twelve months.
 *
 * Consent that never expires is consent given by a person who may no longer
 * exist at that browser, and the supervisory authorities that put a number
 * on it land between six and thirteen months. An answer older than this
 * reads as unasked, which shows the bar again rather than assuming the old
 * yes still holds. The same arithmetic is in the inline bootstrap in both
 * page heads, because that runs long before this module is parsed.
 */
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000

/** Fires on this window whenever the stored choice changes. */
export const CONSENT_CHANGED_EVENT = 'ss:consent-changed'
/** Fires when a person asks to revisit the choice, from a footer link. */
export const CONSENT_OPEN_EVENT = 'ss:consent-open'

export type ConsentChoice = 'granted' | 'denied'

type ConsentWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  __ssStartFullStory?: () => void
  FS?: { shutdown?: () => void }
}

function win(): ConsentWindow | null {
  return typeof window === 'undefined' ? null : (window as ConsentWindow)
}

/**
 * The choice this browser has already made, or null if it has not been asked.
 *
 * Storage throws in private mode and when a browser is set to block site
 * data. Unreadable storage means unasked, which shows the banner again: an
 * extra ask is the safe failure, a silent grant is not.
 */
export function readStoredConsent(): ConsentChoice | null {
  const w = win()
  if (!w) return null
  try {
    const raw = w.localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw !== 'granted' && raw !== 'denied') return null
    /*
      An answer with no readable date, or one older than a year, is not an
      answer any more. Missing counts as expired rather than as fresh: the
      two keys are written together, so a value without its timestamp has
      been edited or half cleared, and the safe reading of a damaged record
      is to ask again.
    */
    const at = Date.parse(w.localStorage.getItem(CONSENT_TIME_KEY) || '')
    if (!Number.isFinite(at) || Date.now() - at > CONSENT_MAX_AGE_MS) return null
    if (w.localStorage.getItem(CONSENT_VERSION_KEY) !== CONSENT_VERSION) return null
    return raw
  } catch {
    return null
  }
}

/**
 * Global Privacy Control, the signal the privacy policy says we respond to
 * and, until now, did not read anywhere in this codebase.
 *
 * It is a legally recognised opt out, so it stands in for a Decline: no
 * banner, nothing loads. It does not overrule an explicit Accept made from
 * the cookie preferences control, because a person's own click is a stronger
 * statement about this site than a browser-wide default.
 */
export function hasGlobalPrivacyControl(): boolean {
  const w = win()
  if (!w) return false
  try {
    return (w.navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  } catch {
    return false
  }
}

/** Whether the measuring tags may run right now. */
export function analyticsAllowed(): boolean {
  return readStoredConsent() === 'granted'
}

/** Whether to put the question in front of this visitor. */
export function shouldAskForConsent(): boolean {
  if (readStoredConsent() !== null) return false
  return !hasGlobalPrivacyControl()
}

function gtag(w: ConsentWindow, ...args: unknown[]): void {
  if (typeof w.gtag === 'function') {
    w.gtag(...args)
    return
  }
  // gtag.js may still be in flight. The dataLayer is the same queue it reads.
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push(args)
}

/**
 * Cookies the measuring tags leave behind, cleared when consent is withdrawn.
 *
 * Withdrawal that leaves the identifier sitting in the browser is not
 * withdrawal, and the policy promises it is as easy as giving. A cookie set
 * on `.superschema.ai` cannot be removed by naming `superschema.ai`, so both
 * are tried, and a failure here is not worth an error: the tags are already
 * switched off by the time this runs.
 */
/**
 * What the measuring tools leave behind, cleared when consent is withdrawn.
 *
 * Withdrawal that leaves the identifier sitting in the browser is not
 * withdrawal, and the cookie policy states in as many words that declining
 * clears what was set. Getting this list short was the bug: it named
 * `fs_uid` and not `fs_lua`, so a withdrawal on production left FullStory's
 * activity cookie and both of its local storage entries in place, under a
 * page that said they were gone. Prefixes now, not names, because these are
 * two vendors' namespaces rather than our own.
 *
 * `ss_` is deliberately absent: the record of the answer has to survive the
 * answer.
 */
const MEASURING_PREFIXES = ['_ga', '_gid', '_gat', '_gcl', 'fs_', '_fs_']

function isMeasuring(name: string): boolean {
  return MEASURING_PREFIXES.some((prefix) => name.startsWith(prefix))
}

/**
 * Clear them from all three places they live.
 *
 * A cookie set on `.superschema.ai` cannot be removed by naming
 * `superschema.ai`, so every domain form is tried. A failure here is not
 * worth an error: the tools are already switched off by the time this runs.
 */
export function clearMeasuringCookies(): void {
  const w = win()
  if (!w || typeof document === 'undefined') return
  try {
    const host = w.location.hostname
    const domains = ['', host, `.${host}`]
    const parts = host.split('.')
    if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`)
    for (const entry of document.cookie.split(';')) {
      const name = entry.split('=')[0]?.trim()
      if (!name || !isMeasuring(name)) continue
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`
      }
    }
  } catch {
    // Nothing to do about it, and nothing depends on it having worked.
  }

  /*
    The recorder keeps the same two identifiers in local storage as well, and
    a tab id in session storage, so clearing only the cookies leaves it able
    to recognise this browser the moment consent is given again.
  */
  for (const store of [w.localStorage, w.sessionStorage]) {
    try {
      /*
        `store.key(i)` rather than Object.keys: some Storage implementations
        do not expose their entries as own properties, and this repo runs on
        one of them, so Object.keys returned nothing and the first version of
        this loop deleted nothing while passing every assertion about
        cookies. Collected first, removed after, because removing during the
        walk shifts the indices under it.
      */
      const doomed: string[] = []
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i)
        if (key && isMeasuring(key)) doomed.push(key)
      }
      for (const key of doomed) store.removeItem(key)
    } catch {
      // Unavailable or blocked. The tools are off either way.
    }
  }
}

export function setConsent(choice: ConsentChoice): void {
  const w = win()
  if (!w) return

  try {
    w.localStorage.setItem(CONSENT_STORAGE_KEY, choice)
    w.localStorage.setItem(CONSENT_TIME_KEY, new Date().toISOString())
    w.localStorage.setItem(CONSENT_VERSION_KEY, CONSENT_VERSION)
  } catch {
    // The choice still holds for this page view, and the banner still closes.
  }

  if (choice === 'granted') {
    gtag(w, 'consent', 'update', { analytics_storage: 'granted' })
    try {
      w.__ssStartFullStory?.()
    } catch {
      // A recorder that will not start is not a reason to break the page.
    }
  } else {
    gtag(w, 'consent', 'update', { analytics_storage: 'denied' })
    try {
      w.FS?.shutdown?.()
    } catch {
      // Already gone, or never started.
    }
    clearMeasuringCookies()
  }

  w.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { choice } }))
}

/** Reopen the question. The footer link on every page calls this. */
export function openCookiePreferences(): void {
  const w = win()
  if (!w) return
  w.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT))
}
